import { createFileRoute } from '@tanstack/react-router';

function PrivacyPage() {
  return (
    <article className="mx-auto max-w-3xl space-y-8">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Legal
        </p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          This notice explains how personal data is processed when you use Brand Generator.
          It is written in plain language because there is very little processing to
          describe.
        </p>
      </header>

      <Section title="1. Controller">
        <p>
          The controller within the meaning of Art. 4 No. 7 GDPR is:
        </p>
        <p>Lukas Schneider, Winzererstr. 28, 80797 Munich, Germany.</p>
        <p>
          Email:{' '}
          <a
            className="underline underline-offset-4 hover:text-foreground"
            href="mailto:lu.schneider@protonmail.com"
          >
            lu.schneider@protonmail.com
          </a>
          .
        </p>
      </Section>

      <Section title="2. What we collect on this site">
        <p>
          Brand Generator does not run its own analytics, does not set cookies, and does not
          embed third-party scripts that would contact us with information about you. The
          interface itself is a single-page application that runs in your browser.
        </p>
        <p>
          Two same-origin endpoints are served by Cloudflare Pages Functions and act as
          stateless network helpers when you actively trigger them:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="text-foreground">
              <code className="font-mono text-xs">/replicate/*</code>
            </strong>{' '}
            forwards your generation requests to{' '}
            <code className="font-mono text-xs">api.replicate.com</code> so the browser does
            not need cross-origin credentials. See section 4.
          </li>
          <li>
            <strong className="text-foreground">
              <code className="font-mono text-xs">/scrape</code>
            </strong>{' '}
            fetches the brand homepage URL you enter for research. See section 5.
          </li>
        </ul>
        <p>
          Neither function reads, logs or stores request bodies, prompts, or your Replicate
          API token. Standard hosting-provider edge logs (IP, timestamp, requested URL,
          user-agent) still apply as described in section 6. Legal basis for the proxy
          traffic: Art. 6 (1) (b) GDPR, performance of the action you requested.
        </p>
      </Section>

      <Section title="3. Local storage in your browser">
        <p>
          The application stores data locally on your device so it can function:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="text-foreground">IndexedDB</strong>: brands, jobs, prompts and
            generated images you create.
          </li>
          <li>
            <strong className="text-foreground">localStorage</strong>: your settings,
            including the Replicate API token you choose to provide.
          </li>
        </ul>
        <p>
          This data never leaves your device. It is not transmitted to us. The legal basis is
          Art. 6 (1) (f) GDPR together with § 25 (2) Nr. 2 TTDSG: this storage is strictly
          necessary to provide the functionality you explicitly requested, so no consent is
          required. You can erase all of it at any time by clearing the site’s storage in
          your browser.
        </p>
      </Section>

      <Section title="4. Replicate (third-party model API)">
        <p>
          When you start a generation job, your browser sends the request to the same-origin
          path <code className="font-mono text-xs">/replicate/*</code>. A stateless Cloudflare
          Pages Function forwards it verbatim to{' '}
          <code className="font-mono text-xs">api.replicate.com</code>, including the{' '}
          <code className="font-mono text-xs">Authorization</code> header that carries your
          Replicate API token. The function does not read, log or store the request body or
          your token; it only rewrites the host so the browser does not need cross-origin
          credentials. Replicate is an independent controller for the data you send them;
          their privacy policy applies:{' '}
          <a
            className="underline underline-offset-4 hover:text-foreground"
            href="https://replicate.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            replicate.com/privacy
          </a>
          .
        </p>
        <p>
          Replicate in turn routes each request to the underlying model provider you select
          in the model picker (currently Anthropic, OpenAI, Google, Black Forest Labs,
          Ideogram, Recraft and ByteDance, depending on the model). Each provider is a
          further independent controller or processor with its own retention and, where
          applicable, training policies. Review their terms before sending sensitive prompts
          or reference images.
        </p>
        <p>
          Legal basis: Art. 6 (1) (b) GDPR, performance of the action you requested.
        </p>
      </Section>

      <Section title="5. Brand research / URL scraping">
        <p>
          When you enter a brand homepage URL for DNA research, your browser asks the
          same-origin path <code className="font-mono text-xs">/scrape</code> to fetch that
          page on your behalf, because most sites block cross-origin browser requests. A
          stateless Cloudflare Pages Function performs a single GET against the URL you
          supplied (HTTP and HTTPS only, response capped at 2 MB, requests to private or
          loopback hosts refused) and returns the HTML to your browser. The function does
          not retain the response body.
        </p>
        <p>
          The fetched website sees a request from Cloudflare's egress IP with the user-agent{' '}
          <code className="font-mono text-xs">
            Mozilla/5.0 (compatible; brand-generator/1.0; +scrape)
          </code>
          . The HTML is then processed locally in your browser and may be sent onward to a
          model via the Replicate proxy (section 4) if you choose to run extraction.
        </p>
        <p>
          Legal basis: Art. 6 (1) (b) GDPR, performance of the action you requested.
        </p>
      </Section>

      <Section title="6. Hosting and server logs">
        <p>
          Brand Generator is delivered through Cloudflare Pages. The hosting provider may,
          on its own legal basis (Art. 6 (1) (f) GDPR, interest in secure and reliable
          operation), record standard request data such as your IP address, timestamp,
          requested URL and user-agent in short-term edge logs. This applies both to static
          asset requests and to calls to the{' '}
          <code className="font-mono text-xs">/replicate/*</code> and{' '}
          <code className="font-mono text-xs">/scrape</code> functions. We do not access or
          aggregate these logs and do not combine them with any other data.
        </p>
      </Section>

      <Section title="7. Fonts">
        <p>
          The fonts used on this site (Inter, Instrument Serif, JetBrains Mono) are
          self-hosted and served from the same origin as the site itself. No font CDN
          (such as Google Fonts) is contacted; no IP address is transmitted to a third party
          to load fonts.
        </p>
      </Section>

      <Section title="8. Your rights under the GDPR">
        <p>
          Under the GDPR you have the rights to information (Art. 15), rectification
          (Art. 16), erasure (Art. 17), restriction of processing (Art. 18), data portability
          (Art. 20), and to object to processing (Art. 21). To exercise any of these rights,
          contact the email address above. Because we do not collect personal data on this
          site, in most cases the practical answer to a request will be that we hold no data
          about you.
        </p>
        <p>
          You also have the right to lodge a complaint with a supervisory authority. The
          competent authority for the controller is:
        </p>
        <p>
          Bayerisches Landesamt für Datenschutzaufsicht (BayLDA), Promenade 18, 91522
          Ansbach, Germany.
        </p>
      </Section>

      <Section title="9. Changes to this policy">
        <p>
          If the way the site works changes (for example, if analytics or accounts are ever
          introduced), this notice will be updated before such processing begins.
        </p>
        <p className="pt-2 font-mono text-xs uppercase tracking-[0.18em]">
          Last updated: 2026-05-06.
        </p>
      </Section>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="space-y-2 text-[15px] leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});
